import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpErrorResponseInterceptor } from 'src/core/interceptors/http-error-response.interceptor';
import { firstValueFrom } from 'rxjs';
import { MetricDefinitionService } from 'src/core/services/metric-definition/metric-definition.service';
import { StateDefinitionCacheService } from 'src/core/services/state-definition/state-definition-cache.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { LocalizationService, provideAbpCore, withOptions } from '@abp/ng.core';
import { provideAbpOAuth } from '@abp/ng.oauth';
import { provideSettingManagementConfig } from '@abp/ng.setting-management/config';
import { provideFeatureManagementConfig } from '@abp/ng.feature-management';
import { provideCommercialUiConfig } from '@volo/abp.commercial.ng.ui/config';
import { provideAccountAdminConfig } from '@volo/abp.ng.account/admin/config';
import { twoFactorBehaviourOptions } from '@volo/abp.ng.account/admin';
import { provideAccountPublicConfig } from '@volo/abp.ng.account/public/config';
import { provideAuditLoggingConfig } from '@volo/abp.ng.audit-logging/config';
import { provideChatConfig, ChatConfigService } from '@volo/abp.ng.chat/config';
import { provideIdentityConfig } from '@volo/abp.ng.identity/config';
import { provideLanguageManagementConfig } from '@volo/abp.ng.language-management/config';
import { registerLocale } from '@volo/abp.ng.language-management/locale';
import { provideOpeniddictproConfig } from '@volo/abp.ng.openiddictpro/config';
import { provideSaasConfig } from '@volo/abp.ng.saas/config';
import { provideTextTemplateManagementConfig } from '@volo/abp.ng.text-template-management/config';
import { provideGdprConfig, withCookieConsentOptions } from '@volo/abp.ng.gdpr/config';
import { provideLogo, withEnvironmentOptions } from '@volo/ngx-lepton-x.core';
import { providePrimeNG } from 'primeng/config';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideUiTour } from 'ngx-ui-tour-primeng';
import { MessageService } from 'primeng/api';
import { PrimeDarkModeService } from 'src/core/services/primeng/primeng-dark-mode.service';
import { PrimeLocaleService } from 'src/core/services/primeng/primeng-locale.service';
import { AURA_PRIME_PRESET } from 'src/core/services/primeng/primeng-presets';
import { AgenticCxChatConfigService } from 'src/core/services/chat/agentic-cx-chat-config.service';
import { environment } from '../environments/environment';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { APP_ROUTES } from './app.routes';
import {
  ThemeLeptonXModule,
  HttpErrorComponent,
  LPX_TRANSLATE_PROVIDER,
} from '@volosoft/abp.ng.theme.lepton-x';
import { SideMenuLayoutModule } from '@volosoft/abp.ng.theme.lepton-x/layouts';
import { LPX_THEMES, LPX_THEME_OPTIONS } from '@volosoft/ngx-lepton-x';
import {
  LPX_CONTAINERS,
  SideMenuLayoutTranslate,
  defineSettingsProviders,
  withSettingsOption,
} from '@volosoft/ngx-lepton-x/layouts';
import {
  ACX_APPEARANCES,
  ACX_THEME_OPTIONS,
} from 'src/core/constants/theme/lpx-appearance.constants';
import { ACX_CONTAINERS } from 'src/core/constants/theme/lpx-container.constants';
import { normalizeLpxPreferences } from 'src/core/helpers/lpx-preferences.helper';
import { AcxSidebarSettingsService } from 'src/core/services/theme/acx-sidebar-settings.service';
import { UiColorThemeService } from 'src/core/services/theme/ui-color-theme.service';
import {
  ThemeSharedModule,
  withHttpErrorConfig,
  provideAbpThemeShared,
} from '@abp/ng.theme.shared';
import { VALIDATION_BLUEPRINT_PROVIDER } from 'src/core/validation/validation-blueprints.provider';
import { loadAppConfig } from './config/app-config';

const PRIME_DARK_MODE_SELECTOR = '.prime-dark';

export const appConfig: ApplicationConfig = {
  providers: [
    // Angular 21: zone.js tabanli change detection artik acikca bildirilmeli.
    provideZoneChangeDetection({ eventCoalescing: true }),
    // ng2-charts 10: NgChartsModule kalkti; Chart.js bilesenleri artik burada kaydediliyor.
    provideCharts(withDefaultRegisterables()),
    // ngx-ui-tour-primeng 3: TourService artik root'ta saglanmiyor, provideUiTour ile kaydediliyor.
    provideUiTour(),
    provideRouter(APP_ROUTES),
    APP_ROUTE_PROVIDER,
    // ABP kanonik sirasi: provideAbpCore, uygulamaya ozgu APP_INITIALIZER'lardan ONCE gelmeli.
    // ABP'nin kendi initializer'i (getInitialData) once kosmali; aksi halde REST cagiran kendi
    // initializer'larimiz ConfigStateService henuz kurulmadan calisip DI dongusu yaratiyor.
    provideAbpCore(
      withOptions({
        environment,
        registerLocaleFn: registerLocale(),
      }),
    ),
    provideAnimations(),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useValue: normalizeLpxPreferences,
    },
    providePrimeNG({
      theme: {
        preset: AURA_PRIME_PRESET,
        options: {
          darkModeSelector: PRIME_DARK_MODE_SELECTOR,
          cssLayer: true,
        },
      },
      zIndex: {
        modal: 8100,
        overlay: 8000,
        menu: 8000,
        tooltip: 8100,
      },
    }),
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [PrimeDarkModeService],
      useFactory: (darkModeService: PrimeDarkModeService) => () => darkModeService.initialize(),
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [PrimeLocaleService],
      useFactory: (localeService: PrimeLocaleService) => () => localeService.initialize(),
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [MetricDefinitionService],
      useFactory: (service: MetricDefinitionService) => () =>
        firstValueFrom(service.getDefinitions(), { defaultValue: [] }),
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [StateDefinitionCacheService, OAuthService],
      useFactory: (service: StateDefinitionCacheService, oAuth: OAuthService) => () => {
        if (!oAuth.hasValidAccessToken()) return Promise.resolve();
        return firstValueFrom(service.getDefinitions(), { defaultValue: [] });
      },
    },
    {
      // ABP's two-factor settings tab renders the raw enum names; localize them via backend keys.
      provide: APP_INITIALIZER,
      multi: true,
      deps: [LocalizationService],
      useFactory: (localization: LocalizationService) => () =>
        twoFactorBehaviourOptions.forEach(option => {
          const name = option.key;
          Object.defineProperty(option, 'key', {
            get: () =>
              localization.instant({ key: `AbpAccount::TwoFactorBehaviour:${name}`, defaultValue: name }),
          });
        }),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => loadAppConfig,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorResponseInterceptor,
      multi: true,
    },
    provideAbpOAuth(),
    provideAccountAdminConfig(),
    provideAccountPublicConfig(),
    provideIdentityConfig(),
    provideLanguageManagementConfig(),
    provideSaasConfig(),
    provideAuditLoggingConfig(),
    provideOpeniddictproConfig(),
    provideTextTemplateManagementConfig(),
    provideSettingManagementConfig(),
    provideCommercialUiConfig(),
    provideFeatureManagementConfig(),
    provideChatConfig(),
    { provide: ChatConfigService, useClass: AgenticCxChatConfigService },
    provideGdprConfig(
      withCookieConsentOptions({
        privacyPolicyUrl: 'gdpr-cookie-consent/privacy',
        cookiePolicyUrl: 'gdpr-cookie-consent/cookie',
      }),
    ),
    provideLogo(withEnvironmentOptions(environment)),
    importProvidersFrom(
      ThemeLeptonXModule.forRoot(),
      SideMenuLayoutModule.forRoot(),
      ThemeSharedModule,
    ),
    { provide: LPX_THEME_OPTIONS, useValue: ACX_THEME_OPTIONS },
    { provide: LPX_THEMES, useValue: ACX_APPEARANCES },
    { provide: LPX_CONTAINERS, useValue: ACX_CONTAINERS },
    defineSettingsProviders(
      withSettingsOption({
        generalSettingsTranslateKey: SideMenuLayoutTranslate.GeneralSettings,
        settingsService: AcxSidebarSettingsService,
        settingsMenuEventName: 'click',
      }),
    ),
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [UiColorThemeService],
      useFactory: (colorTheme: UiColorThemeService) => () => colorTheme.initialize(),
    },
    LPX_TRANSLATE_PROVIDER,
    MessageService,
    provideAbpThemeShared(
      VALIDATION_BLUEPRINT_PROVIDER,
      withHttpErrorConfig({
        errorScreen: {
          component: HttpErrorComponent,
          forWhichErrors: [401, 403, 404, 500],
          hideCloseIcon: true,
        },
      }),
    ),
  ],
};
